import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import PaginationLoader from "component/PaginationLoader";
import { ScopeClientInvoices, ClientHideInvoiceApi, ClientInvoiceDetailApi, ClientInvoiceListViewApi, OpenClientInvoiceDetails, OpenPartialPaidInvoice, SaleActivityApi } from "store/slices/clientinvoiceSlice";
import config from "../../../../config";
import ApiStatus from "component/ApiStatus";
import { clientDetailInvoicePrintApi } from "store/slices/printerSlice";
import ClientPaidInvoice from "./ClientPaidInvoice";
import PartialPaidInvoice from "./PartialPaidInvoice";
import InvoiceActivityTimeline from "./InvoiceActivityTimeline";
import RefundFromInvoiceModal from "./RefundFromInvoiceModal";
import PayByStripeModalClient from "./PayByStripeModalClient";
import getStripeClient from "pages/getStripeClient";
import Elements from "pages/SalonStripeElements";
import CompletedSaleRefundModal from "./CompletedSaleRefundModal";
import "react-spinners";
import { clientDetailId, normalizeClientDetail, readableClientHistoryError } from "../clientDetailUx";
import { formatClientInvoiceAmount, formatClientInvoiceDate } from "../../../../services/clientInvoiceContract";
import { defaultClientStatementDateRange, validateClientStatementDateRange } from "./clientStatementPrintUx";
import { buildClientInvoiceDisplay } from "./clientInvoiceDisplay";

const openPdfPayload = (payload, filename) => {
  if (payload && typeof payload === "object" && typeof payload.html === "string") {
    const anchor = document.createElement("a");
    anchor.href = `data:application/pdf;base64,${payload.html}`;
    anchor.download = filename;
    anchor.click();
    return;
  }

  const blob = payload instanceof Blob
    ? payload
    : new Blob([payload], { type: "application/pdf" });
  const objectUrl = URL.createObjectURL(blob);
  const opened = window.open(objectUrl, "_blank", "noopener,noreferrer");
  if (!opened) {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.click();
  }
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
};

const Invoices = () => {
  const category = useSelector(state => state.clientinvoice.invoiceCategory) || "completed";
  const [loading, setLoading] = useState(false);
  const [printError, setPrintError] = useState("");
  const [loader, setLoader] = useState({});
  const [detailErrors, setDetailErrors] = useState({});
  const dispatch = useDispatch();
  const store = useStore();
  const { t } = useTranslation();
  const auth = useSelector((state) => state.auth);
  const currentUser = auth.user;
  void (currentUser && currentUser.role_id);
  const stripePromise = getStripeClient();
  useSelector(state => state.salonmodule.isAccess);
  const clientinvoiceApiStatus = useSelector((state) => state.clientinvoice.isApiStatus);
  const storedInvoices = useSelector((state) => state.clientinvoice.isListView);
  const invoiceClientId = useSelector((state) => state.clientinvoice.invoiceClientId);
  const invoiceListError = useSelector((state) => state.clientinvoice.isListViewError);
  const invoiceVisibilityError = useSelector((state) => state.clientinvoice.isVisibilityError);
  const isAdminUser = ["true", "1", "yes"].includes(String(localStorage.getItem("ADMIN") || "").toLowerCase());
  const isOpenClientInvoiceDetailsTab = useSelector((state) => state.clientinvoice.isOpenClientInvoiceDetailsTab);
  const isOpenPartialPaidInvoice = useSelector((state) => state.clientinvoice.isOpenPartialPaidInvoice);
  const detail = normalizeClientDetail(useSelector((state) => state.client.isDetailData));
  const client_id = clientDetailId(detail);
  const currentClient = useRef(client_id);
  const profileVersion = useRef(0);
  const printSequence = useRef(0);
  if (currentClient.current !== client_id) {
    profileVersion.current += 1;
    currentClient.current = client_id;
  }
  const owned = Boolean(client_id) && (invoiceClientId == null
    ? storedInvoices?.data?.every(row => Number(row.client_id) === Number(client_id))
    : invoiceClientId === Number(client_id));
  const invoiceViews = owned ? storedInvoices : null;
  useLayoutEffect(() => { setLoading(false); setPrintError(""); setLoader({}); setDetailErrors({}); setOpenActivity({}); dispatch(ScopeClientInvoices(client_id)); }, [client_id, dispatch]);
  useEffect(() => () => { currentClient.current = null; dispatch(ScopeClientInvoices(null)); }, [dispatch]);
  const currentDetailRequest = action => {
    const state = store.getState().clientinvoice;
    return currentClient.current === client_id && state.invoiceClientId === Number(client_id)
      && state.detailRequestId === action?.meta?.requestId;
  };
  const openInvoiceDocument = async (invoice, index) => {
    if (!Number.isSafeInteger(Number(invoice.id)) || Number(invoice.id) <= 0) return;
    const version = profileVersion.current;
    setLoader(previous => ({ ...previous, [index]: true }));
    setDetailErrors(previous => ({ ...previous, [index]: "" }));
    const action = await dispatch(ClientInvoiceDetailApi({ client_id, id: invoice.id }));
    if (currentClient.current !== client_id || profileVersion.current !== version) return;
    setLoader(previous => ({ ...previous, [index]: false }));
    if (!currentDetailRequest(action)) return;
    if (action?.meta?.requestStatus !== "fulfilled") {
      setDetailErrors(previous => ({ ...previous, [index]: readableClientHistoryError(action.payload, t("Unable to open this invoice. Please try again.")) }));
      return;
    }
    const returnedInvoice = action.payload;
    if (Number(returnedInvoice?.id) !== Number(invoice.id) || Number(returnedInvoice?.client_id) !== Number(client_id)) {
      setDetailErrors(previous => ({ ...previous, [index]: t("The returned invoice does not match this client and invoice. Please try again.") }));
      return;
    }
    const depositDocument = returnedInvoice.status !== "Paid" && (returnedInvoice.status === "Partial" || Number(returnedInvoice.is_deposit_refund) === 1);
    dispatch(depositDocument ? OpenPartialPaidInvoice("open") : OpenClientInvoiceDetails("open"));
  };
  const isOpenClientInvoiceRefundModal = useSelector((state) => state.clientinvoice.isOpenClientInvoiceRefundModal);
  const isOpenStripeRefundCompletedSaleModal = useSelector((state) => state.clientinvoice.isOpenStripeRefundCompletedSaleModal);
  const isOpenClientInvoiceStripePayModalClient = useSelector((state) => state.clientinvoice.isOpenClientInvoiceStripePayModalClient);

  const invoiceListParams = () => {
    // Flask validates this field as a JSON boolean; omit it for normal users
    // so the backend keeps its active-only default.
    return isAdminUser
      ? { client_id, category, include_inactive: true }
      : { client_id, category };
  };

  const fetchDataInvoiceList = () => {
    if (!client_id || !invoiceViews?.next_page_url) return;
    dispatch(ClientInvoiceListViewApi({ ...invoiceListParams(), next_page_url: invoiceViews.next_page_url }));
  };

  const [isFetching, setIsFetching] = useState(false);
  const [openActivity, setOpenActivity] = useState({});
  const saleActivity = useSelector((state) => state.clientinvoice.saleActivity);
  const loadMoreItemsList = () => {
    if (!client_id) return;
    setIsFetching(true);
    dispatch(ClientInvoiceListViewApi({ ...invoiceListParams(), next_page_url: invoiceViews && invoiceViews.next_page_url }));
    //mocking an API call
    setTimeout(() => {
      setIsFetching(false);
    }, 2000);
  };

  const printStatement = async (event) => {
    event.preventDefault();
    const range = defaultClientStatementDateRange(new Date(), currentUser?.salon?.timezone);
    const validationError = validateClientStatementDateRange(range);
    if (validationError) {
      setPrintError(validationError);
      return;
    }
    setPrintError("");
    setLoading(true);
    const version = profileVersion.current;
    const sequence = ++printSequence.current;
    const action = await dispatch(clientDetailInvoicePrintApi({
      client_id,
      ...range,
    }));
    if (currentClient.current !== client_id || profileVersion.current !== version || printSequence.current !== sequence) return;
    setLoading(false);
    if (action.meta.requestStatus === "fulfilled") {
      openPdfPayload(action.payload, "client-statement.pdf");
    } else {
      setPrintError(readableClientHistoryError(action.payload, t("Unable to print the client statement.")));
    }
  };
  return (
    <>
      <div className="drawer-header">
        <h2 className="mb-4 pe-md-5 mb-lg-5">
          {t("Invoices")}
          <a href="#" className="btn btn-outline btn-sm ms-2" onClick={printStatement} aria-busy={loading}>
            {loading ? t("Preparing...") : t("Print Statement")}
          </a>
        </h2>
      </div>
      <div role="tablist" aria-label={t("Invoice lists")} className="d-flex gap-2 px-4 mb-3">
        {[["completed", "Invoices"], ["open", "Open Invoices"]].map(([value, label]) => (
          <button key={value} type="button" role="tab" aria-selected={category === value}
            className={category === value ? "btn btn-primary" : "btn btn-outline-primary"}
            onClick={() => {
              if (value === category) return;
              setLoader({}); setDetailErrors({}); setOpenActivity({});
              dispatch(ClientInvoiceListViewApi({ ...invoiceListParams(), category: value }));
            }}>{t(label)}</button>
        ))}
      </div>
      <div className="content-wrp" id="invoicelist">
        {printError && <div className="alert alert-danger" role="alert">{printError}</div>}
        {owned && invoiceVisibilityError && <div className="alert alert-danger" role="alert">{t("Invoice was not changed.")} {invoiceVisibilityError} {t("Check the details and try again.")}</div>}
        {owned && invoiceListError && <div className="alert alert-danger" role="alert">{readableClientHistoryError(invoiceListError, t("Unable to load invoices."))}</div>}
        {invoiceViews && Array.isArray(invoiceViews.data) && invoiceViews.data.length > 0 ? (
          <InfiniteScroll className="content-wrp row gx-0" dataLength={invoiceViews.data.length} next={fetchDataInvoiceList} scrollableTarget="invoicelist" hasMore={invoiceViews.next_page_url ? true : false} loader={<PaginationLoader />}>
            {Object.keys(invoiceViews.data).map((item, i) => {
              const invoice = invoiceViews.data[item] || {};
              const invoiceDisplay = buildClientInvoiceDisplay(invoice);
              let id = invoice.id;
              // let invoice = invoiceViews.data[item].invoice;
              // let invoice_name = invoice && invoice.name;
              // let invoice_credit = invoice && invoice.credit;
              // let cost = invoiceViews.data[item].cost;
              //let client_id = invoiceViews.data[item].client_id;
              //let note = invoiceViews.data[item].note;
              let invoicedate = invoice.invoicedate;
              let total_cost = invoice.cart_sum_cost;

              let status = invoice.status;
              let is_active = invoice.is_active;
              if(!total_cost && status === "Partial"){
                total_cost = `${formatClientInvoiceAmount(invoice.totaldeposit)} (Deposit)`;
              }
              const displayTotal = typeof total_cost === "string" ? total_cost : formatClientInvoiceAmount(total_cost);
              void (invoice.appointments && invoice.appointments.length > 0 ? invoice.appointments[0] : []);



              return (
                <div className="client-invoice-position position-relative" key={item && item.id ? item.id : i}>
                  <div className="event-box" data-id={id}>
                    <div className="d-flex align-items-start">
                      <div className="w-100 px-md-3 px-2">
                        <h6 className="mb-1 color-wine fw-semibold">{invoiceDisplay.invoiceNumber}</h6>
                        <h6 className="mb-1">
                          {t("Invoice date:")} {formatClientInvoiceDate(invoicedate)}
                        </h6>
                        <h6 className="mb-0 fw-semibold">
                          ${displayTotal}
                        </h6>
                        {invoiceDisplay.note ? (
                          <h6 className="mb-0 fw-normal text-muted text-break">{invoiceDisplay.note}</h6>
                        ) : null}
                      </div>
                      <span className="active">{is_active === 1 || is_active === "1" ? invoiceDisplay.status : t("Inactive")}</span>
                      {isAdminUser && (
                        <>
                          {is_active === 1 || is_active === "1" ? (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm ms-2"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                dispatch(ClientHideInvoiceApi({ id, client_id, is_active: 0 }));
                              }}
                            >
                              {t("Delete")}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-outline-success btn-sm ms-2"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const version = profileVersion.current;
                                dispatch(ClientHideInvoiceApi({ id, client_id, is_active: 1 })).then((action) => {
                                  if (action.meta.requestStatus === "fulfilled" && currentClient.current === client_id && profileVersion.current === version) {
                                    dispatch(ClientInvoiceListViewApi(invoiceListParams()));
                                  }
                                });
                              }}
                            >
                              {t("Active")}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {Number.isSafeInteger(Number(id)) && Number(id) > 0 && (
                    <button type="button" className="btn p-0 cursor-pointer invoice-link invoice-tab-icon-position"
                      aria-label={`View invoice ${invoiceDisplay.invoiceNumber}`} aria-busy={Boolean(loader[i])}
                      disabled={Boolean(loader[i])} onClick={() => openInvoiceDocument(invoice, i)}>
                      <img src={config.imagepath + "past.png"} alt="" />
                    </button>
                  )}
                  {detailErrors[i] && <div role="alert" className="alert alert-danger">{detailErrors[i]}</div>}
                  <div className="invoice-activity-toggle">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      aria-expanded={openActivity[id] ? "true" : "false"}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const next = !openActivity[id];
                        setOpenActivity((prev) => ({ ...prev, [id]: next }));
                        // Read the audit trail from the live sale/activity route.
                        if (next && !saleActivity[id]) dispatch(SaleActivityApi({ id }));
                      }}
                    >
                      {t("Activity")}
                    </button>
                    {openActivity[id] && (
                      <InvoiceActivityTimeline
                        activity={(saleActivity[id] && saleActivity[id].entries) || []}
                        status={saleActivity[id]?.status}
                        onRetry={() => dispatch(SaleActivityApi({ id }))}
                      />
                    )}
                  </div>
                  {loader[i] ? (
                  <div className="position-absolute" style={{ right:'15px',bottom:'25px' }}>
                  <span className="spinner-border spinner-border-sm"></span>
                  </div>
                  ) : null}
                </div>
              );
            })}
            {!isFetching && invoiceViews.next_page_url && (
              <div className="col-2 m-auto p-3 text-center">
                <button onClick={loadMoreItemsList} className="btn btn-primary">
                  {t("More")}
                </button>
              </div>
            )}
          </InfiniteScroll>
        ) : (
          <>
            {!owned || (clientinvoiceApiStatus &&
              clientinvoiceApiStatus.ClientInvoiceListViewApi === "loading") ? (
              <ApiStatus actionType="clientinvoiceview" />
            ) : (
              <div className="complete-box text-center d-flex flex-column justify-content-center mt-xl-4">
                <div className="complete-box-wrp text-center">
                  <img src={config.imagepath + "nots.png"} alt="" className="mb-md-4 mb-3" />
                  <h5 className="mb-2 fw-semibold">{t("There are no invoices available.")}</h5>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {owned && isOpenClientInvoiceDetailsTab && <ClientPaidInvoice />}
      {owned && isOpenPartialPaidInvoice && <PartialPaidInvoice />}
      {owned && isOpenClientInvoiceRefundModal && (<Elements stripe={stripePromise}><RefundFromInvoiceModal /></Elements>)}
      {owned && isOpenStripeRefundCompletedSaleModal && (<Elements stripe={stripePromise}><CompletedSaleRefundModal /></Elements>)}
      {owned && isOpenClientInvoiceStripePayModalClient && (<Elements stripe={stripePromise}><PayByStripeModalClient /></Elements>)}
    </>
  );
};

export default Invoices;
